import { Alert, AlertDescription, AlertTitle, Button, Checkbox, cn, Switch } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, TriangleAlert } from 'lucide-react';
import { type MouseEvent, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { mmProgramPath } from '@/lib/paths';
import { uniqueSorted } from '@/utils/array';
import { pluralize } from '@/utils/string';
import { boundedPeriod, type Period, periodBounds, scheduleEndBounds, scheduleStartBounds } from '../date-bounds';
import { SESSION_LABELS } from '../mm-program-editing';
import { STATUS_META } from '../schedule-timeline';
import { AddProgramsDialog } from './add-programs-dialog';
import { BulkEditDialog } from './bulk-edit-dialog';
import { formatFieldValue, GridCell } from './grid-cell';
import { type FieldGroup, type FieldValue, GRID_FIELDS, type GridField } from './grid-fields';
import {
  type AddSchedulesResult,
  applyBulkEdit,
  applyFieldEdit,
  buildRows,
  currentOf,
  type DocumentProblems,
  fieldValueOf,
  isCellChanged,
  locateProblems,
  overlapsPeriod,
  type ProgramRow,
  type StatusFilter,
  statusFilterOf,
} from './grid-rows';
import { reviewAndSave, type SaveFailure, SaveFailuresAlert, UnsavedEditsBar } from './save-programs';
import { FilterSelect, PeriodFilter, StatusFilterMenu } from './schedule-filters';
import { editableOf, type ProgramDrafts } from './use-program-drafts';

const ALL = 'all';
const ALL_HOURS = 'none';
const DEFAULT_STATUSES: StatusFilter[] = ['inForce', 'upcoming'];
const FIXED_COLUMNS = 5;
const GROUP_TONES: Record<FieldGroup, string> = {
  Schedule: '',
  B1: 'bg-sky-500/5',
  B2: 'bg-indigo-500/5',
  Index: 'bg-violet-500/5',
  Pay: 'bg-amber-500/5',
  'Open volume': 'bg-emerald-500/5',
  Rest: 'bg-rose-500/5',
  Linger: 'bg-orange-500/5',
  Skew: 'bg-lime-500/5',
  Volatility: 'bg-cyan-500/5',
  'Out of book': 'bg-fuchsia-500/5',
};
const NBBO_SUFFIX = '+½ NBBO';
// Sticky offsets: the select, market maker and instrument columns stay in view while scrolling sideways.
const STICKY = {
  select: 'sticky left-0 z-10 w-9 min-w-9',
  name: 'sticky left-9 z-10 w-40 min-w-40',
  instrument: 'sticky left-[12.25rem] z-10 w-36 min-w-36 shadow-[1px_0_0_hsl(var(--border))]',
};

const groupsOf = (fields: GridField[]) =>
  fields.reduce<{ group: FieldGroup; span: number }[]>((groups, field) => {
    const last = groups.at(-1);
    if (last?.group === field.group) last.span += 1;
    else groups.push({ group: field.group, span: 1 });
    return groups;
  }, []);

const suffixOf = (row: ProgramRow, field: GridField) => {
  const bucket = field.key === 'b1.bps' ? 'b1' : field.key === 'b2.bps' ? 'b2' : null;
  return bucket && row.program[bucket].nbbo === true ? NBBO_SUFFIX : undefined;
};

const boundsOf = (row: ProgramRow, field: GridField) =>
  field.key === 'start_date' ? scheduleStartBounds(row.program) : field.key === 'end_date' ? scheduleEndBounds(row.program) : undefined;

const sessionLabel = (row: ProgramRow) => (row.program.session ? (SESSION_LABELS[row.program.session] ?? row.program.session) : 'All hours');

interface AllProgramsGridProps {
  drafts: ProgramDrafts;
  isLoading: boolean;
  onReload: (pid: string) => void;
}

export const AllProgramsGrid = ({ drafts, isLoading, onReload }: AllProgramsGridProps) => {
  const queryClient = useQueryClient();
  const { documents, edited } = drafts;
  const [marketMaker, setMarketMaker] = useState(ALL);
  const [instrument, setInstrument] = useState(ALL);
  const [session, setSession] = useState(ALL);
  const [statuses, setStatuses] = useState<StatusFilter[]>(DEFAULT_STATUSES);
  const [period, setPeriod] = useState<Period>({ from: '', to: '' });
  const [onlyEdited, setOnlyEdited] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [failures, setFailures] = useState<SaveFailure[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const anchor = useRef<string | null>(null);

  const rows = useMemo(() => buildRows(documents), [documents]);
  const periodLimits = periodBounds(statuses);
  const appliedPeriod = boundedPeriod(period, periodLimits);
  const problems = useMemo(() => new Map<string, DocumentProblems>(documents.map((document) => [document.pid, locateProblems(document.pid, currentOf(document))])), [documents]);
  const editedPids = useMemo(() => new Set(edited.map((document) => document.pid)), [edited]);
  const editedProblems = edited.reduce((total, document) => total + (problems.get(document.pid)?.count ?? 0), 0);

  const rowProblems = (row: ProgramRow) => problems.get(row.pid)?.rows.get(row.id) ?? [];
  const cellProblem = (row: ProgramRow, field: GridField) => problems.get(row.pid)?.cells.get(`${row.id}|${field.path}`);
  const hasRowEdits = (row: ProgramRow) => editedPids.has(row.pid) && GRID_FIELDS.some((field) => isCellChanged(row, field));

  const visible = rows.filter(
    (row) =>
      (marketMaker === ALL || row.pid === marketMaker) &&
      (instrument === ALL || row.instrument === instrument) &&
      (session === ALL || (session === ALL_HOURS ? !row.program.session : row.program.session === session)) &&
      statuses.includes(statusFilterOf(row.status)) &&
      overlapsPeriod(row.program, appliedPeriod.from, appliedPeriod.to) &&
      (!onlyEdited || hasRowEdits(row)),
  );
  const selectable = visible.filter((row) => !row.isReadOnly);
  const selectedRows = rows.filter((row) => selected.has(row.id));
  const selectedVisible = selectable.filter((row) => selected.has(row.id)).length;

  const marketMakerOptions = [
    { value: ALL, label: 'All market makers' },
    ...uniqueSorted(rows.map((row) => `${row.name}\u0000${row.pid}`)).map((key) => {
      const [name, pid] = key.split('\u0000');
      return { value: pid, label: name };
    }),
  ];
  const instrumentOptions = [{ value: ALL, label: 'All instruments' }, ...uniqueSorted(rows.map((row) => row.instrument)).map((code) => ({ value: code, label: code }))];
  const sessionOptions = [
    { value: ALL, label: 'All sessions' },
    { value: ALL_HOURS, label: 'All hours only' },
    ...Object.entries(SESSION_LABELS).map(([value, label]) => ({ value, label })),
  ];

  const toggleRow = (row: ProgramRow, event: MouseEvent) => {
    const next = new Set(selected);
    const isSelecting = !selected.has(row.id);
    const from = anchor.current ? selectable.findIndex((item) => item.id === anchor.current) : -1;
    const to = selectable.findIndex((item) => item.id === row.id);
    const range = event.shiftKey && from >= 0 && to >= 0 ? selectable.slice(Math.min(from, to), Math.max(from, to) + 1) : [row];
    for (const item of range) {
      if (isSelecting) next.add(item.id);
      else next.delete(item.id);
    }
    anchor.current = row.id;
    setSelected(next);
  };
  const toggleAll = () => {
    const next = new Set(selected);
    const isSelecting = selectedVisible < selectable.length;
    for (const row of selectable) {
      if (isSelecting) next.add(row.id);
      else next.delete(row.id);
    }
    setSelected(next);
  };

  const commit = (row: ProgramRow, field: GridField, value: FieldValue) => {
    const document = documents.find((item) => item.pid === row.pid);
    if (!document) return;
    drafts.setDocuments(new Map([[row.pid, applyFieldEdit(editableOf(document), row, field, value)]]));
  };

  const applyBulk = (values: Map<string, FieldValue>) => {
    const start = new Map(documents.map((document) => [document.pid, editableOf(document)]));
    const result = applyBulkEdit(selectedRows, start, values);
    drafts.setDocuments(new Map([...result.documents].filter(([pid, document]) => document !== start.get(pid))));
    setIsBulkEditing(false);
    toast.success(
      `Updated ${pluralize(result.changedRows, 'schedule')}${result.skippedRows ? `; skipped ${pluralize(result.skippedRows, 'schedule')} without those fields or edit rights` : ''}. Review and save when ready.`,
    );
  };

  // Shows only the market maker's unsaved rows, so the added schedules and the ones they end are what is left to fill in and check.
  const addPrograms = (pid: string, result: AddSchedulesResult) => {
    drafts.setDocuments(new Map([[pid, result.document]]));
    setIsAdding(false);
    setMarketMaker(pid);
    setInstrument(ALL);
    setSession(ALL);
    // A schedule ended on today's start counts as ended, and is one of the rows to check.
    setStatuses(['inForce', 'upcoming', 'ended']);
    setPeriod({ from: '', to: '' });
    setOnlyEdited(true);
    toast.success(
      `Added ${pluralize(result.added, 'schedule')}${result.skipped.length ? `; skipped ${pluralize(result.skipped.length, 'instrument')}` : ''}. Fill in the cells marked red, then review and save.`,
    );
  };

  const save = async () => {
    setIsSaving(true);
    try {
      setFailures(await reviewAndSave(edited, queryClient));
    } finally {
      setIsSaving(false);
    }
  };

  const reload = (pid: string) => {
    drafts.discard(pid);
    setFailures((previous) => previous.filter((failure) => failure.pid !== pid));
    onReload(pid);
  };

  // An edited market maker's cell problems are already marked red on the cells being edited, so only the ones with no cell are listed.
  const problemList = documents.flatMap((document) => {
    const found = problems.get(document.pid);
    if (!found?.count) return [];
    const isEdited = editedPids.has(document.pid);
    const messages = uniqueSorted([...(isEdited ? [] : found.cells.values()), ...[...found.rows.values()].flat(), ...found.document]);
    return messages.length ? [{ pid: document.pid, name: currentOf(document).name, isEdited, messages }] : [];
  });
  const listsEditedProblems = problemList.some((item) => item.isEdited);

  if (isLoading && !rows.length) return <LoadingPanel className="py-24" />;

  return (
    <div className="flex flex-col gap-4">
      <SaveFailuresAlert failures={failures} onReload={reload} />

      {problemList.length ? (
        <Alert variant={listsEditedProblems ? 'destructive' : 'default'}>
          <AlertTitle>{listsEditedProblems ? 'To fix before saving' : 'Stored settings the scoring job would reject'}</AlertTitle>
          <AlertDescription>
            <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto text-sm">
              {problemList.flatMap((item) =>
                item.messages.map((message) => (
                  <li key={`${item.pid}:${message}`}>
                    <span className="font-medium">{item.name}</span>
                    {item.isEdited ? null : <span className="text-muted-foreground"> (stored)</span>}: {message}
                  </li>
                )),
              )}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect label="Market maker" onChange={setMarketMaker} options={marketMakerOptions} value={marketMaker} />
        <FilterSelect label="Instrument" onChange={setInstrument} options={instrumentOptions} value={instrument} />
        <FilterSelect label="Session" onChange={setSession} options={sessionOptions} value={session} />
        <StatusFilterMenu onChange={setStatuses} value={statuses} />
        <PeriodFilter bounds={periodLimits} onChange={setPeriod} value={period} />
        <span className="flex items-center gap-1.5 text-xs">
          <Switch checked={onlyEdited} id="grid-only-edited" onCheckedChange={setOnlyEdited} />
          <label htmlFor="grid-only-edited">Unsaved only</label>
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {pluralize(visible.length, 'schedule')} shown{selected.size ? ` · ${selected.size} selected` : ''}
        </span>
        <div className="flex gap-2">
          {selected.size ? (
            <Button onClick={() => setSelected(new Set())} size="sm" type="button" variant="ghost">
              Clear selection
            </Button>
          ) : null}
          <Button className="gap-1.5" disabled={!selectedRows.some((row) => !row.isReadOnly)} onClick={() => setIsBulkEditing(true)} size="sm" type="button" variant="outline">
            <Pencil className="size-3.5" />
            Edit {selected.size ? pluralize(selected.size, 'selected row') : 'selected rows'}
          </Button>
          <Button className="gap-1.5" disabled={!documents.some((document) => document.canEdit)} onClick={() => setIsAdding(true)} size="sm" type="button" variant="outline">
            <Plus className="size-3.5" />
            Add programs
          </Button>
        </div>
      </div>

      <div className="max-h-[70vh] overflow-auto rounded-md border border-border">
        <table className="w-max min-w-full border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 z-20 bg-card">
            <tr className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <th className={cn(STICKY.select, 'bg-card')} />
              <th className={cn(STICKY.name, 'bg-card')} />
              <th className={cn(STICKY.instrument, 'bg-card')} />
              <th className="bg-card" colSpan={FIXED_COLUMNS - 3} />
              {groupsOf(GRID_FIELDS).map(({ group, span }) => (
                <th className={cn('border-l border-border px-1.5 pt-2 text-left', GROUP_TONES[group])} colSpan={span} key={group}>
                  {group}
                </th>
              ))}
            </tr>
            <tr className="text-left text-xs text-muted-foreground">
              <th className={cn(STICKY.select, 'border-b border-border bg-card px-2')}>
                <Checkbox
                  aria-label="Select all shown schedules"
                  checked={selectedVisible && selectedVisible === selectable.length ? true : selectedVisible ? 'indeterminate' : false}
                  disabled={!selectable.length}
                  onClick={toggleAll}
                />
              </th>
              <th className={cn(STICKY.name, 'border-b border-border bg-card px-2 py-1.5 font-medium')}>Market maker</th>
              <th className={cn(STICKY.instrument, 'border-b border-border bg-card px-2 py-1.5 font-medium')}>Instrument</th>
              <th className="border-b border-border px-2 py-1.5 font-medium">Session</th>
              <th className="border-b border-border px-2 py-1.5 font-medium">Status</th>
              {GRID_FIELDS.map((field) => (
                <th
                  className={cn('border-b border-l border-border/40 px-1.5 py-1.5 font-medium whitespace-nowrap', GROUP_TONES[field.group])}
                  key={field.key}
                  title={field.description}
                >
                  {field.label}
                  {field.unit ? <span className="font-normal"> ({field.unit})</span> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, position) => {
              const issues = rowProblems(row);
              const isSelected = selected.has(row.id);
              const continuesSet = row.sessionSet === 'first' || row.sessionSet === 'middle';
              const nextIsSameSet = continuesSet && visible[position + 1]?.sessionSet !== null && visible[position + 1]?.sessionSet !== 'first';
              const rowTone = cn(isSelected ? 'bg-primary/10' : row.status === 'upcoming' ? 'bg-blue-500/5' : row.status === 'scored' ? 'bg-muted/40' : 'bg-card');
              const stickyTone = isSelected ? 'bg-[hsl(var(--card))] [background-image:linear-gradient(hsl(var(--primary)/0.1),hsl(var(--primary)/0.1))]' : 'bg-card';
              return (
                <tr key={row.id} className={cn(rowTone, '[&>td]:border-b', nextIsSameSet ? '[&>td]:border-b-transparent' : '[&>td]:border-border')}>
                  <td className={cn(STICKY.select, stickyTone, 'px-2', row.sessionSet && 'border-l-4 border-l-violet-500')}>
                    <Checkbox
                      aria-label={`Select ${row.name} ${row.instrument} ${sessionLabel(row)}`}
                      checked={isSelected}
                      disabled={row.isReadOnly}
                      onClick={(event) => toggleRow(row, event)}
                    />
                  </td>
                  <td className={cn(STICKY.name, stickyTone, 'px-2 py-1')}>
                    <Link className="block truncate text-xs font-medium hover:underline" title={row.pid} to={mmProgramPath(row.pid)}>
                      {row.name}
                    </Link>
                    {row.active ? null : <span className="text-[10px] text-muted-foreground">Inactive</span>}
                  </td>
                  <td className={cn(STICKY.instrument, stickyTone, 'px-2 py-1')}>
                    <span className="flex items-center gap-1 font-mono text-xs">
                      {row.instrument}
                      {issues.length ? (
                        <span className="inline-flex" title={issues.join('\n')}>
                          <TriangleAlert aria-label={issues.join('\n')} className="size-3.5 shrink-0 text-red-500" role="img" />
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-2 py-1">
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-[11px] whitespace-nowrap',
                        row.program.session ? 'bg-violet-500/15 font-medium text-violet-700 dark:text-violet-300' : 'text-muted-foreground',
                      )}
                    >
                      {sessionLabel(row)}
                    </span>
                  </td>
                  <td className="px-2 py-1">
                    <span className={cn('rounded-full border px-2 py-0.5 text-[11px] whitespace-nowrap', STATUS_META[row.status].className)}>{STATUS_META[row.status].label}</span>
                  </td>
                  {GRID_FIELDS.map((field) => {
                    const applies = field.appliesTo(row.program, row.instrument);
                    const savedValue = row.saved && field.appliesTo(row.saved, row.instrument) ? field.get(row.saved) : undefined;
                    return (
                      <GridCell
                        applies={applies}
                        bounds={boundsOf(row, field)}
                        field={field}
                        isChanged={editedPids.has(row.pid) && isCellChanged(row, field)}
                        isReadOnly={row.isReadOnly}
                        key={field.key}
                        onCommit={(value) => commit(row, field, value)}
                        problem={cellProblem(row, field)}
                        savedText={row.saved ? formatFieldValue(field, savedValue) : 'not saved yet'}
                        suffix={suffixOf(row, field)}
                        value={fieldValueOf(row, field)}
                      />
                    );
                  })}
                </tr>
              );
            })}
            {visible.length ? null : (
              <tr>
                <td className="px-4 py-10 text-center text-sm text-muted-foreground" colSpan={FIXED_COLUMNS + GRID_FIELDS.length}>
                  No schedules match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <UnsavedEditsBar isSaving={isSaving} names={edited.map((document) => currentOf(document).name)} onDiscard={() => drafts.discard()} onSave={save} problems={editedProblems} />

      {isBulkEditing ? <BulkEditDialog onApply={applyBulk} onClose={() => setIsBulkEditing(false)} rows={selectedRows} /> : null}
      {isAdding ? <AddProgramsDialog documents={documents} onAdd={addPrograms} onClose={() => setIsAdding(false)} /> : null}
    </div>
  );
};
AllProgramsGrid.displayName = 'AllProgramsGrid';
